import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  LayoutList,
  Palette,
  RefreshCw,
  Search,
  ExternalLink,
  Share2,
  Home,
  Globe,
  Users,
  Layers,
  Image,
  Mail,
  FileText,
  Save,
} from "lucide-react";
import { useSiteContent } from "@/context/SiteContentContext";
import { deepMerge } from "@/lib/deepMerge";
import { translations, contactInfo as defaultContact } from "@/i18n/translations";
import { servicePages as defaultServicePages } from "@/i18n/services";
import { notifyContentChanged } from "@/lib/contentSync";
import { CUSTOMIZER_PAGES, CUSTOMIZER_SECTIONS } from "@/admin/contentManifest";
import { deleteDot, formatSourceSnippet, getDot, setDot } from "@/admin/lib/payloadPaths";
import { fetchSitePayload, normalizePayload } from "@/admin/adminPayload";
import { reconcile } from "@/admin/lib/reconcile";
import { sanitizePayload } from "@/lib/sanitizePayload";
import AdminPageHeader, { AdminCard, AdminCardBody, AdminCardHeader } from "@/admin/components/AdminPageHeader";
import {
  AdminBadge,
  AdminLangToggle,
  AdminPanel,
  AdminPreviewBox,
  AdminSubPanel,
  AdminTabBar,
  adminBtnPrimary,
  adminBtnSecondary,
  adminBtnUpload,
  adminEyebrow,
  adminInput,
  adminLabel,
  adminRange,
  adminTextarea,
} from "@/admin/components/AdminFormUi";
import ListEditor from "@/admin/ListEditor";
import { normalizeStatsListForEditor } from "@/lib/statsContent";

function normalizeListForEditor(path, items, lang = "ro") {
  if (!Array.isArray(items)) return [];
  if (path === "marquee") {
    return items.map((x) => (typeof x === "string" ? { value: x } : { value: String(x?.value ?? "") }));
  }
  if (path === "services_page.trust_points") {
    return items.map((x) => (typeof x === "string" ? { value: x } : { value: String(x?.value ?? "") }));
  }
  if (path === "stats.items") {
    return normalizeStatsListForEditor(items, lang);
  }
  return items;
}

function denormalizeListForSave(path, items) {
  if (path === "marquee") {
    return items.map((r) => String(r.value ?? "").trim()).filter(Boolean);
  }
  if (path === "services_page.trust_points") {
    return items.map((r) => String(r.value ?? "").trim()).filter(Boolean);
  }
  return items;
}

function readEffectiveList(section, lang, draft) {
  const path = section.listEditor?.path;
  if (!path) return [];
  let merged;
  if (section.domain === "translation") {
    merged = deepMerge(translations[lang], draft[lang] || {});
  } else if (section.domain === "servicePage") {
    const base = defaultServicePages[lang]?.[section.slug] || {};
    const ov = draft.servicePages?.[lang]?.[section.slug] || {};
    merged = deepMerge(base, ov);
  } else {
    return [];
  }
  const raw = getDot(merged, path);
  const fallback =
    section.domain === "translation"
      ? getDot(translations[lang], path)
      : section.domain === "servicePage"
        ? getDot(defaultServicePages[lang]?.[section.slug], path)
        : undefined;
  const arr = Array.isArray(raw) ? raw : Array.isArray(fallback) ? fallback : [];
  return normalizeListForEditor(path, arr, lang);
}

function applyListToDraft(draft, section, lang, items) {
  const path = section.listEditor?.path;
  if (!path) return draft;
  const value = denormalizeListForSave(path, items);
  const out = { ...draft };
  if (section.domain === "translation") {
    out[lang] = setDot(draft[lang] || {}, path, value);
    return out;
  }
  if (section.domain === "servicePage") {
    const sp = draft.servicePages || { ro: {}, en: {} };
    const branch = sp[lang] || {};
    const fullPath = `${section.slug}.${path}`;
    out.servicePages = { ...sp, [lang]: setDot(branch, fullPath, value) };
  }
  return out;
}

function readToggleEffective(draft, path) {
  const merged = deepMerge(translations.ro, draft.ro || {});
  const v = getDot(merged, path);
  return v !== false;
}

function readGlobalSettingEffective(draft, path, fallback) {
  const merged = deepMerge(translations.ro, draft.ro || {});
  const v = getDot(merged, path);
  if (v === undefined || v === null || v === "") return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function readDraftSlice(draft, section, lang, dotPath) {
  if (section.domain === "translation") return getDot(draft[lang], dotPath);
  if (section.domain === "contact") return getDot(draft.contactInfo, dotPath);
  if (section.domain === "servicePage") return getDot(draft.servicePages?.[lang]?.[section.slug], dotPath);
  return undefined;
}

function readDefault(section, lang, dotPath) {
  if (section.domain === "translation") return getDot(translations[lang], dotPath);
  if (section.domain === "contact") return getDot(defaultContact, dotPath);
  if (section.domain === "servicePage") return getDot(defaultServicePages[lang]?.[section.slug], dotPath);
  return undefined;
}

function readEffective(section, lang, draft, dotPath) {
  let merged;
  if (section.domain === "translation") {
    merged = deepMerge(translations[lang], draft[lang] || {});
  } else if (section.domain === "contact") {
    merged = deepMerge({ ...defaultContact }, draft.contactInfo || {});
  } else if (section.domain === "servicePage") {
    const base = defaultServicePages[lang]?.[section.slug] || {};
    const ov = draft.servicePages?.[lang]?.[section.slug] || {};
    merged = deepMerge(base, ov);
  } else {
    return undefined;
  }
  const eff = getDot(merged, dotPath);
  if (eff !== undefined && eff !== null) return eff;
  return readDefault(section, lang, dotPath);
}

function sourceLabel(section) {
  if (section.domain === "translation") return "translations.js";
  if (section.domain === "contact") return "translations.js (contactInfo)";
  return "services.js";
}

function applyDraftValue(draft, section, lang, dotPath, value) {
  const out = { ...draft };
  if (section.domain === "translation") {
    out[lang] = setDot(draft[lang] || {}, dotPath, value);
    return out;
  }
  if (section.domain === "contact") {
    out.contactInfo = setDot(draft.contactInfo || {}, dotPath, value);
    return out;
  }
  if (section.domain === "servicePage") {
    const sp = draft.servicePages || { ro: {}, en: {} };
    const branch = sp[lang] || {};
    const fullPath = `${section.slug}.${dotPath}`;
    out.servicePages = { ...sp, [lang]: setDot(branch, fullPath, value) };
    return out;
  }
  return out;
}

function removeDraftValue(draft, section, lang, dotPath) {
  const out = { ...draft };
  if (section.domain === "translation") {
    out[lang] = deleteDot(draft[lang] || {}, dotPath);
    return out;
  }
  if (section.domain === "contact") {
    out.contactInfo = deleteDot(draft.contactInfo || {}, dotPath);
    return out;
  }
  if (section.domain === "servicePage") {
    const sp = draft.servicePages || { ro: {}, en: {} };
    const branch = sp[lang] || {};
    const fullPath = `${section.slug}.${dotPath}`;
    out.servicePages = { ...sp, [lang]: deleteDot(branch, fullPath) };
    return out;
  }
  return out;
}

function hasDbOverride(draft, section, lang, dotPath) {
  return readDraftSlice(draft, section, lang, dotPath) !== undefined;
}

const MAIN_TABS = [
  { id: "copy", label: "Texte și imagini", Icon: LayoutList },
  { id: "seo", label: "SEO & share", Icon: Share2 },
  { id: "brand", label: "Logo și favicon", Icon: Palette },
];

const PAGE_ICONS = {
  global: Globe,
  home: Home,
  about: Users,
  services: Layers,
  portfolio: Image,
  contact: Mail,
  legal: FileText,
};

function SeoSharePanel({ draft, setDraft, uploadKey, setUploadKey, saving }) {
  return (
    <AdminPanel
      title="SEO & share"
      description="Titlul și descrierea apar în Google și când trimiți linkul pe WhatsApp / rețele sociale. Imagine share recomandată: 1200×630 px (raport 1.91:1)."
    >
      <div className="grid gap-6 lg:grid-cols-2">
        {["ro", "en"].map((seoLang) => {
          const labels =
            seoLang === "ro"
              ? {
                  lang: "Română",
                  siteTitle: "Titlu site (Google)",
                  metaDescription: "Descriere Google (meta)",
                  shareTitle: "Titlu share (opțional)",
                  shareDescription: "Descriere share (opțional)",
                  shareImageUrl: "Imagine share",
                }
              : {
                  lang: "English",
                  siteTitle: "Site title (Google)",
                  metaDescription: "Google description (meta)",
                  shareTitle: "Share title (optional)",
                  shareDescription: "Share description (optional)",
                  shareImageUrl: "Share image",
                };
          const block = draft.branding?.seo?.[seoLang] || {};
          const setSeo = (key, value) =>
            setDraft((d) => ({
              ...d,
              branding: {
                ...(d.branding || {}),
                seo: {
                  ...(d.branding?.seo || {}),
                  [seoLang]: { ...(d.branding?.seo?.[seoLang] || {}), [key]: value },
                },
              },
            }));
          return (
            <AdminSubPanel key={seoLang} title={labels.lang}>
              <label className="block">
                <span className={adminLabel}>{labels.siteTitle}</span>
                <input
                  type="text"
                  value={block.siteTitle || ""}
                  onChange={(e) => setSeo("siteTitle", e.target.value)}
                  className={adminInput}
                />
              </label>
              <label className="block">
                <span className={adminLabel}>{labels.metaDescription}</span>
                <textarea
                  value={block.metaDescription || ""}
                  onChange={(e) => setSeo("metaDescription", e.target.value)}
                  rows={2}
                  className={adminTextarea}
                />
              </label>
              <label className="block">
                <span className={adminLabel}>{labels.shareTitle}</span>
                <input
                  type="text"
                  value={block.shareTitle || ""}
                  onChange={(e) => setSeo("shareTitle", e.target.value)}
                  placeholder="Gol = titlu Google"
                  className={adminInput}
                />
              </label>
              <label className="block">
                <span className={adminLabel}>{labels.shareDescription}</span>
                <textarea
                  value={block.shareDescription || ""}
                  onChange={(e) => setSeo("shareDescription", e.target.value)}
                  rows={2}
                  placeholder="Gol = descriere Google"
                  className={adminTextarea}
                />
              </label>
              <label className="block">
                <span className={adminLabel}>{labels.shareImageUrl}</span>
                <p className="mb-2 text-[10px] leading-snug text-[#3e2723]/50">
                  1200×630 px · JPG/PNG/WebP · preview link WhatsApp, Facebook
                </p>
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={block.shareImageUrl || ""}
                    onChange={(e) => setSeo("shareImageUrl", e.target.value)}
                    placeholder="/logo_sd.png sau /api/uploads/…"
                    className={`${adminInput} min-w-0 flex-1`}
                  />
                  <label className={adminBtnUpload}>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      className="sr-only"
                      disabled={uploadKey === `brand:seo-${seoLang}` || saving}
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        e.target.value = "";
                        if (!file) return;
                        setUploadKey(`brand:seo-${seoLang}`);
                        try {
                          const fd = new FormData();
                          fd.append("file", file);
                          const r = await fetch("/api/admin/upload.php", {
                            method: "POST",
                            credentials: "include",
                            body: fd,
                          });
                          const j = await r.json();
                          if (r.ok && j.ok && j.url) setSeo("shareImageUrl", j.url);
                        } finally {
                          setUploadKey(null);
                        }
                      }}
                    />
                    {uploadKey === `brand:seo-${seoLang}` ? "…" : "Upload"}
                  </label>
                </div>
                {block.shareImageUrl ? (
                  <img
                    src={block.shareImageUrl}
                    alt=""
                    className="mt-3 max-h-32 max-w-full rounded-xl border border-[#3e2723]/10 object-contain"
                  />
                ) : null}
              </label>
            </AdminSubPanel>
          );
        })}
      </div>
    </AdminPanel>
  );
}

export default function AdminCustomizer() {
  const { reload } = useSiteContent();
  const [lang, setLang] = useState("ro");
  const [draft, setDraft] = useState(null);
  const [msg, setMsg] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mainTab, setMainTab] = useState("copy");
  const [activePageId, setActivePageId] = useState("home");
  const [activeSectionId, setActiveSectionId] = useState("home-settings");
  const [sectionQuery, setSectionQuery] = useState("");
  const [uploadKey, setUploadKey] = useState(null);
  // Snapshot of the payload as loaded, used to reconcile only this session's
  // changes at save time (so concurrent admins don't clobber each other).
  const baselineRef = useRef(null);

  const loadDraft = useCallback(async () => {
    setLoading(true);
    setMsg("");
    try {
      const p = await fetchSitePayload();
      baselineRef.current = JSON.parse(JSON.stringify(p));
      setDraft(p);
    } catch {
      setMsg("Nu s-a putut încărca conținutul din baza de date.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDraft();
  }, [loadDraft]);

  const onSave = async () => {
    if (!draft) return;
    setSaving(true);
    setMsg("");
    try {
      const draftNorm = normalizePayload(draft);
      const baseline = baselineRef.current || draftNorm;

      // Pull the freshest server state and apply ONLY this session's changes on
      // top of it, so another admin's concurrent edits are preserved.
      let body = draftNorm;
      try {
        const serverNow = await fetchSitePayload();
        body = normalizePayload(reconcile(serverNow, baseline, draftNorm));
      } catch {
        // If the refetch fails, fall back to saving the local draft.
        body = draftNorm;
      }
      body = sanitizePayload(body);

      const r = await fetch("/api/admin/site-save.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) {
        setMsg(j.detail || "Salvare eșuată.");
        return;
      }
      // Refresh public payload + cross-tab signal
      reload();
      notifyContentChanged();

      // Verify immediately by refetching DB payload into the editor.
      // This prevents the “I saved, refreshed, and it reverted” confusion.
      try {
        const verified = await fetchSitePayload();
        baselineRef.current = JSON.parse(JSON.stringify(verified));
        setDraft(verified);
        setMsg("Salvat și verificat din DB. Site-ul public se actualizează (semnal + reîncărcare la ~12s).");
      } catch {
        baselineRef.current = JSON.parse(JSON.stringify(body));
        setMsg("Salvat. (Nu am putut re-verifica din DB în acest moment.)");
      }
    } catch {
      setMsg("Eroare de rețea.");
    } finally {
      setSaving(false);
    }
  };

  const onUploadForListItem = async (section, index, fieldKey, file) => {
    if (!file || !draft) return;
    const key = `${section.id}:list:${index}:${fieldKey}`;
    setUploadKey(key);
    setMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch("/api/admin/upload.php", { method: "POST", credentials: "include", body: fd });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) {
        setMsg(j.detail || "Upload eșuat.");
        return;
      }
      setDraft((d) => {
        const current = readEffectiveList(section, lang, d);
        const next = current.map((row, i) => (i === index ? { ...row, [fieldKey]: j.url } : row));
        return applyListToDraft(d, section, lang, next);
      });
      setMsg(`Fișier încărcat: ${j.url}`);
    } catch {
      setMsg("Eroare la upload.");
    } finally {
      setUploadKey(null);
    }
  };

  const onUploadForField = async (e, section, dotPath) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !draft) return;
    const key = `${section.id}:${dotPath}`;
    setUploadKey(key);
    setMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch("/api/admin/upload.php", { method: "POST", credentials: "include", body: fd });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) {
        setMsg(j.detail || "Upload eșuat.");
        return;
      }
      setDraft((d) => applyDraftValue(d, section, lang, dotPath, j.url));
      setMsg(`Fișier încărcat: ${j.url}`);
    } catch {
      setMsg("Eroare la upload.");
    } finally {
      setUploadKey(null);
    }
  };

  const onUploadLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !draft) return;
    setUploadKey("brand:logo");
    setMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch("/api/admin/upload.php", { method: "POST", credentials: "include", body: fd });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) {
        setMsg(j.detail || "Upload eșuat.");
        return;
      }
      setDraft((d) => ({
        ...d,
        contactInfo: { ...(d.contactInfo || {}), logoUrl: j.url },
      }));
      setMsg(`Logo încărcat: ${j.url}`);
    } catch {
      setMsg("Eroare la upload.");
    } finally {
      setUploadKey(null);
    }
  };

  const onUploadFooterLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !draft) return;
    setUploadKey("brand:footer-logo");
    setMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch("/api/admin/upload.php", { method: "POST", credentials: "include", body: fd });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) {
        setMsg(j.detail || "Upload eșuat.");
        return;
      }
      setDraft((d) => ({
        ...d,
        contactInfo: { ...(d.contactInfo || {}), footerLogoUrl: j.url },
      }));
      setMsg(`Logo footer încărcat: ${j.url}`);
    } catch {
      setMsg("Eroare la upload.");
    } finally {
      setUploadKey(null);
    }
  };

  const onUploadFavicon = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !draft) return;
    setUploadKey("brand:favicon");
    setMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch("/api/admin/upload.php", { method: "POST", credentials: "include", body: fd });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) {
        setMsg(j.detail || "Upload eșuat.");
        return;
      }
      setDraft((d) => ({
        ...d,
        branding: { ...(d.branding || {}), faviconUrl: j.url },
      }));
      setMsg(`Favicon încărcat: ${j.url}`);
    } catch {
      setMsg("Eroare la upload.");
    } finally {
      setUploadKey(null);
    }
  };

  const LOGO_H_MIN = 28;
  const LOGO_H_MAX = 160;
  const LOGO_H_DEFAULT = 56;

  const setLogoHeightPx = useCallback((px) => {
    const n = Math.round(Number(px));
    if (!Number.isFinite(n)) return;
    const clamped = Math.min(LOGO_H_MAX, Math.max(LOGO_H_MIN, n));
    setDraft((d) => {
      const b = { ...(d.branding || {}) };
      delete b.logoWidthPx;
      b.logoHeightPx = clamped;
      return { ...d, branding: b };
    });
  }, []);

  const resetLogoSize = useCallback(() => {
    setDraft((d) => {
      const b = { ...(d.branding || {}) };
      delete b.logoWidthPx;
      delete b.logoHeightPx;
      return { ...d, branding: b };
    });
  }, []);

  const logoSliderHeight = useMemo(() => {
    const b = draft?.branding;
    if (!b || typeof b !== "object") return LOGO_H_DEFAULT;
    const h = typeof b.logoHeightPx === "number" ? b.logoHeightPx : null;
    const w = typeof b.logoWidthPx === "number" ? b.logoWidthPx : null;
    const raw = h ?? w ?? LOGO_H_DEFAULT;
    return Math.min(LOGO_H_MAX, Math.max(LOGO_H_MIN, raw));
  }, [draft?.branding]);

  const hasCustomLogoSize = useMemo(() => {
    const b = draft?.branding;
    if (!b || typeof b !== "object") return false;
    return typeof b.logoHeightPx === "number" || typeof b.logoWidthPx === "number";
  }, [draft?.branding]);

  const FOOTER_H_MIN = 24;
  const FOOTER_H_MAX = 120;
  const FOOTER_H_DEFAULT = 40;

  const setFooterLogoHeightPx = useCallback((px) => {
    const n = Math.round(Number(px));
    if (!Number.isFinite(n)) return;
    const clamped = Math.min(FOOTER_H_MAX, Math.max(FOOTER_H_MIN, n));
    setDraft((d) => {
      const b = { ...(d.branding || {}) };
      delete b.footerLogoWidthPx;
      b.footerLogoHeightPx = clamped;
      return { ...d, branding: b };
    });
  }, []);

  const resetFooterLogoSize = useCallback(() => {
    setDraft((d) => {
      const b = { ...(d.branding || {}) };
      delete b.footerLogoWidthPx;
      delete b.footerLogoHeightPx;
      return { ...d, branding: b };
    });
  }, []);

  const footerLogoSliderHeight = useMemo(() => {
    const b = draft?.branding;
    if (!b || typeof b !== "object") return FOOTER_H_DEFAULT;
    const h = typeof b.footerLogoHeightPx === "number" ? b.footerLogoHeightPx : null;
    const w = typeof b.footerLogoWidthPx === "number" ? b.footerLogoWidthPx : null;
    const raw = h ?? w ?? FOOTER_H_DEFAULT;
    return Math.min(FOOTER_H_MAX, Math.max(FOOTER_H_MIN, raw));
  }, [draft?.branding]);

  const hasCustomFooterLogoSize = useMemo(() => {
    const b = draft?.branding;
    if (!b || typeof b !== "object") return false;
    return typeof b.footerLogoHeightPx === "number" || typeof b.footerLogoWidthPx === "number";
  }, [draft?.branding]);

  const sectionsForPage = useMemo(
    () => CUSTOMIZER_SECTIONS.filter((s) => s.page === activePageId),
    [activePageId]
  );

  const sectionsFiltered = useMemo(() => {
    const q = sectionQuery.trim().toLowerCase();
    const base = sectionQuery ? CUSTOMIZER_SECTIONS : sectionsForPage;
    if (!q) return base;
    return base.filter(
      (s) =>
        s.title.toLowerCase().includes(q) ||
        (s.subtitle && s.subtitle.toLowerCase().includes(q)) ||
        s.id.toLowerCase().includes(q)
    );
  }, [sectionQuery, sectionsForPage, activePageId]);

  const activePage = useMemo(
    () => CUSTOMIZER_PAGES.find((p) => p.id === activePageId) || CUSTOMIZER_PAGES[0],
    [activePageId]
  );

  const activeSection = useMemo(() => {
    const hit = sectionsFiltered.find((s) => s.id === activeSectionId);
    if (hit) return hit;
    return sectionsFiltered[0] || CUSTOMIZER_SECTIONS[0];
  }, [sectionsFiltered, activeSectionId]);

  useEffect(() => {
    if (!activeSection) return;
    if (!sectionsFiltered.some((s) => s.id === activeSectionId)) {
      setActiveSectionId(activeSection.id);
    }
  }, [sectionsFiltered, activeSection, activeSectionId]);

  useEffect(() => {
    if (sectionQuery) return;
    const first = sectionsForPage[0];
    if (first && !sectionsForPage.some((s) => s.id === activeSectionId)) {
      setActiveSectionId(first.id);
    }
  }, [activePageId, sectionsForPage, activeSectionId, sectionQuery]);

  const logoSrc =
    typeof draft?.contactInfo?.logoUrl === "string" && draft.contactInfo.logoUrl.trim()
      ? draft.contactInfo.logoUrl.trim()
      : "/logo_sd.png";

  const footerLogoPreviewSrc =
    typeof draft?.contactInfo?.footerLogoUrl === "string" && draft.contactInfo.footerLogoUrl.trim()
      ? draft.contactInfo.footerLogoUrl.trim()
      : logoSrc;

  if (loading || !draft) {
    return (
      <div className="mx-auto max-w-6xl py-16 text-center text-sm text-[#3e2723]/55">
        {loading ? "Se încarcă datele din baza de date…" : "Nu există date de afișat."}
      </div>
    );
  }

  return (
    <div className="relative mx-auto min-w-0 max-w-7xl overflow-x-clip pb-32 text-[#3e2723] sm:pb-28">
      <AdminPageHeader
        eyebrow="Content studio"
        title="Personalizare conținut"
        description="Editează texte, imagini, SEO și branding. Modificările se aplică pe site după salvare."
      />

      <div className="mt-6 border-b border-[#3e2723]/8 pb-4">
        <AdminTabBar tabs={MAIN_TABS} activeId={mainTab} onChange={setMainTab} />
      </div>

      <div className="mt-6">
      {mainTab === "seo" ? (
        <SeoSharePanel
          draft={draft}
          setDraft={setDraft}
          uploadKey={uploadKey}
          setUploadKey={setUploadKey}
          saving={saving}
        />
      ) : mainTab === "brand" ? (
        <>
        <div className="grid gap-6 lg:grid-cols-2">
          <AdminPanel
            title="Logo header"
            description="Folosit în bara de navigare. Încarcă PNG / SVG / WebP sau URL public."
          >
            <AdminPreviewBox>
              <img
                src={logoSrc}
                alt=""
                style={{ height: hasCustomLogoSize ? logoSliderHeight : 56, width: "auto", maxWidth: "min(180px, 100%)" }}
                className="object-contain object-left"
                onError={(e) => {
                  e.currentTarget.src = "/logo_sd.png";
                }}
              />
              <div className="min-w-0 flex-1 space-y-2">
                <label className="block">
                  <span className={adminEyebrow}>URL logo</span>
                  <input
                    type="url"
                    value={typeof draft.contactInfo?.logoUrl === "string" ? draft.contactInfo.logoUrl : ""}
                    onChange={(e) =>
                      setDraft((d) => ({
                        ...d,
                        contactInfo: { ...(d.contactInfo || {}), logoUrl: e.target.value },
                      }))
                    }
                    placeholder="/logo_sd.png sau /api/uploads/…"
                    className={`${adminInput} mt-1.5`}
                  />
                </label>
              </div>
            </AdminPreviewBox>
            <div className="mt-5 space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <label className="text-xs font-medium text-[#3e2723]/65" htmlFor="logo-size-range">
                  Mărime logo (înălțime)
                </label>
                <span className="rounded-lg bg-[#fbd9e5]/50 px-2 py-0.5 font-mono text-xs font-semibold text-[#3e2723]">
                  {hasCustomLogoSize ? `${logoSliderHeight}px` : `${LOGO_H_DEFAULT}px · previzualizare`}
                </span>
              </div>
              <input
                id="logo-size-range"
                type="range"
                min={LOGO_H_MIN}
                max={LOGO_H_MAX}
                step={2}
                value={logoSliderHeight}
                onChange={(e) => setLogoHeightPx(e.target.value)}
                className={adminRange}
              />
              <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                <p className="text-[11px] text-[#3e2723]/50">
                  {hasCustomLogoSize
                    ? "Trage bara pentru a redimensiona. Se salvează la „Salvează”."
                    : "Glisează pentru a seta o dimensiune personalizată."}
                </p>
                <button
                  type="button"
                  onClick={resetLogoSize}
                  disabled={!hasCustomLogoSize}
                  className={`${adminBtnSecondary} disabled:cursor-not-allowed disabled:opacity-40`}
                >
                  Implicit din temă
                </button>
              </div>
            </div>
            <label className={`${adminBtnSecondary} mt-4 cursor-pointer`}>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml,.svg"
                className="sr-only"
                disabled={uploadKey === "brand:logo" || saving}
                onChange={onUploadLogo}
              />
              {uploadKey === "brand:logo" ? "Se încarcă…" : "Încarcă fișier logo"}
            </label>
          </AdminPanel>

          <AdminPanel
            title="Favicon"
            description="ICO, PNG sau SVG. După salvare, unele browsere păstrează cache — folosește reîncărcare forțată."
          >
            <label className="block">
              <span className={adminEyebrow}>URL favicon</span>
              <input
                type="url"
                value={typeof draft.branding?.faviconUrl === "string" ? draft.branding.faviconUrl : ""}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    branding: { ...(d.branding || {}), faviconUrl: e.target.value },
                  }))
                }
                placeholder="/favicon.ico sau /api/uploads/…"
                className={`${adminInput} mt-1.5`}
              />
            </label>
            {draft.branding?.faviconUrl ? (
              <p className="mt-4 text-[11px] text-[#3e2723]/55">
                Preview:{" "}
                <img src={draft.branding.faviconUrl} alt="" className="ml-1 inline h-6 w-6 align-middle object-contain" />
              </p>
            ) : null}
            <label className={`${adminBtnSecondary} mt-4 cursor-pointer`}>
              <input
                type="file"
                accept="image/x-icon,image/vnd.microsoft.icon,image/png,image/svg+xml,.ico,.png,.svg"
                className="sr-only"
                disabled={uploadKey === "brand:favicon" || saving}
                onChange={onUploadFavicon}
              />
              {uploadKey === "brand:favicon" ? "Se încarcă…" : "Încarcă favicon"}
            </label>
            <button
              type="button"
              className="mt-3 text-xs font-medium text-[#3e2723]/55 underline decoration-[#3e2723]/20 underline-offset-2 hover:text-[#3e2723]"
              onClick={() =>
                setDraft((d) => {
                  const b = { ...(d.branding || {}) };
                  delete b.faviconUrl;
                  return { ...d, branding: b };
                })
              }
            >
              Revino la favicon implicit din build
            </button>
          </AdminPanel>
        </div>

        <AdminPanel
          className="mt-6"
          title="Logo footer"
          description="Opțional: alt fișier decât logo-ul din header. Dacă URL e gol, se folosește același logo."
        >
          <AdminPreviewBox>
            <img
              src={footerLogoPreviewSrc}
              alt=""
              style={{
                height: hasCustomFooterLogoSize ? footerLogoSliderHeight : 40,
                width: "auto",
                maxWidth: "min(200px, 100%)",
              }}
              className="object-contain object-left"
              onError={(e) => {
                e.currentTarget.src = "/logo_sd.png";
              }}
            />
            <div className="min-w-0 flex-1 space-y-2">
              <label className="block">
                <span className={adminEyebrow}>URL logo footer (opțional)</span>
                <input
                  type="url"
                  value={typeof draft.contactInfo?.footerLogoUrl === "string" ? draft.contactInfo.footerLogoUrl : ""}
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      contactInfo: { ...(d.contactInfo || {}), footerLogoUrl: e.target.value },
                    }))
                  }
                  placeholder="Lasă gol pentru același logo ca header"
                  className={`${adminInput} mt-1.5`}
                />
              </label>
            </div>
          </AdminPreviewBox>
          <div className="mt-5 space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <label className="text-xs font-medium text-[#3e2723]/65" htmlFor="footer-logo-size-range">
                Mărime logo footer (înălțime)
              </label>
              <span className="rounded-lg bg-[#fbd9e5]/50 px-2 py-0.5 font-mono text-xs font-semibold text-[#3e2723]">
                {hasCustomFooterLogoSize ? `${footerLogoSliderHeight}px` : `${FOOTER_H_DEFAULT}px · previzualizare`}
              </span>
            </div>
            <input
              id="footer-logo-size-range"
              type="range"
              min={FOOTER_H_MIN}
              max={FOOTER_H_MAX}
              step={2}
              value={footerLogoSliderHeight}
              onChange={(e) => setFooterLogoHeightPx(e.target.value)}
              className={adminRange}
            />
            <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
              <button
                type="button"
                onClick={resetFooterLogoSize}
                disabled={!hasCustomFooterLogoSize}
                className={`${adminBtnSecondary} disabled:cursor-not-allowed disabled:opacity-40`}
              >
                Implicit din temă (footer)
              </button>
              <label className={`${adminBtnSecondary} cursor-pointer`}>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml,.svg"
                  className="sr-only"
                  disabled={uploadKey === "brand:footer-logo" || saving}
                  onChange={onUploadFooterLogo}
                />
                {uploadKey === "brand:footer-logo" ? "Se încarcă…" : "Încarcă logo footer"}
              </label>
            </div>
          </div>
        </AdminPanel>

        <AdminPanel
          className="mt-6"
          title="Rețele sociale (footer)"
          description="Link-uri complete (ex. https://instagram.com/…). Goale = iconița nu apare."
        >
          <div className="grid gap-4 sm:grid-cols-2">
            {[
              { key: "socialInstagramUrl", label: "Instagram", placeholder: "https://instagram.com/…" },
              { key: "socialFacebookUrl", label: "Facebook", placeholder: "https://facebook.com/…" },
              { key: "socialTiktokUrl", label: "TikTok", placeholder: "https://tiktok.com/@…" },
              { key: "socialLinkedinUrl", label: "LinkedIn", placeholder: "https://linkedin.com/company/…" },
            ].map(({ key, label, placeholder }) => (
              <label key={key} className="block">
                <span className={adminLabel}>{label}</span>
                <input
                  type="url"
                  value={typeof draft.contactInfo?.[key] === "string" ? draft.contactInfo[key] : ""}
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      contactInfo: { ...(d.contactInfo || {}), [key]: e.target.value },
                    }))
                  }
                  placeholder={placeholder}
                  className={adminInput}
                />
              </label>
            ))}
          </div>
        </AdminPanel>
        </>
      ) : (
        <div className="flex min-w-0 flex-col gap-5 xl:flex-row xl:items-start xl:gap-6">
          <aside className="hidden w-full shrink-0 xl:block xl:w-[17.5rem]">
            <div className="sticky top-[5.5rem] max-h-[calc(100vh-6.5rem)] space-y-4 overflow-y-auto overscroll-contain rounded-2xl border border-[#3e2723]/10 bg-white p-4 shadow-[0_8px_30px_-18px_rgba(62,39,35,0.18)]">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#3e2723]/35" />
                <input
                  value={sectionQuery}
                  onChange={(e) => setSectionQuery(e.target.value)}
                  placeholder="Caută secțiune…"
                  className={`${adminInput} py-2 pl-9 text-xs`}
                />
              </div>
              {!sectionQuery ? (
                <div>
                  <p className={adminEyebrow}>Pagini</p>
                  <div className="mt-2 space-y-1">
                    {CUSTOMIZER_PAGES.map((p) => {
                      const Icon = PAGE_ICONS[p.id] || Globe;
                      const active = activePageId === p.id;
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => setActivePageId(p.id)}
                          className={`flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-xs font-semibold transition ${
                            active
                              ? "bg-[#3e2723] text-[#fbd9e5] shadow-sm"
                              : "text-[#3e2723]/70 hover:bg-[#fbd9e5]/35 hover:text-[#3e2723]"
                          }`}
                        >
                          <Icon className="h-4 w-4 shrink-0 opacity-85" aria-hidden />
                          {p.title}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}
              <div>
                <p className={adminEyebrow}>{sectionQuery ? "Rezultate" : "Secțiuni"}</p>
                <div className="mt-2 max-h-[min(50vh,480px)] space-y-1 overflow-y-auto overscroll-contain pr-0.5">
                  {sectionsFiltered.map((s) => {
                    const active = activeSection?.id === s.id;
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => {
                          setActiveSectionId(s.id);
                          if (s.page) setActivePageId(s.page);
                        }}
                        className={`flex w-full flex-col rounded-xl px-3 py-2.5 text-left text-xs transition ${
                          active
                            ? "bg-[#fbd9e5] text-[#3e2723] ring-1 ring-[#3e2723]/10"
                            : "text-[#3e2723]/65 hover:bg-[#faf8f8]"
                        }`}
                      >
                        <span className="font-semibold">{s.title}</span>
                        {s.subtitle ? (
                          <span className={`mt-0.5 line-clamp-2 text-[11px] ${active ? "text-[#3e2723]/65" : "text-[#3e2723]/45"}`}>
                            {s.subtitle}
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </aside>

          {/* Mobile: pagină + secțiune */}
          <div className="grid gap-3 sm:grid-cols-2 xl:hidden">
            <label className="block rounded-2xl border border-[#3e2723]/10 bg-white p-4 shadow-[0_8px_30px_-18px_rgba(62,39,35,0.12)]">
              <span className={adminEyebrow}>Pagină</span>
              <select
                value={activePageId}
                onChange={(e) => setActivePageId(e.target.value)}
                className={`${adminInput} mt-2 h-11 font-medium`}
              >
                {CUSTOMIZER_PAGES.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.title}
                  </option>
                ))}
              </select>
            </label>
            <label className="block rounded-2xl border border-[#3e2723]/10 bg-white p-4 shadow-[0_8px_30px_-18px_rgba(62,39,35,0.12)]">
              <span className={adminEyebrow}>Secțiune</span>
              <select
                value={activeSection?.id || ""}
                onChange={(e) => {
                  const id = e.target.value;
                  const sec = sectionsForPage.find((s) => s.id === id) || CUSTOMIZER_SECTIONS.find((s) => s.id === id);
                  if (sec) {
                    setActiveSectionId(sec.id);
                    if (sec.page) setActivePageId(sec.page);
                  }
                }}
                className={`${adminInput} mt-2 h-11 font-medium`}
              >
                {sectionsForPage.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.title}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="min-w-0 flex-1 space-y-4 overflow-x-clip">
            <AdminCard>
              <AdminCardBody className="flex flex-wrap items-center justify-between gap-4 py-4">
                <div>
                  <p className={adminEyebrow}>Limbă editată</p>
                  <div className="mt-2">
                    <AdminLangToggle lang={lang} onChange={setLang} />
                  </div>
                </div>
                <button type="button" onClick={loadDraft} className={adminBtnSecondary}>
                  <RefreshCw className="h-3.5 w-3.5" aria-hidden />
                  Reîncarcă din DB
                </button>
              </AdminCardBody>
            </AdminCard>

            {activeSection ? (
              <AdminCard>
                <AdminCardHeader>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className={adminEyebrow}>{activePage?.title}</p>
                      <h2 className="font-display text-base font-bold text-[#3e2723] sm:text-lg">{activeSection.title}</h2>
                      {activeSection.subtitle ? (
                        <p className="mt-1 text-xs text-[#3e2723]/55">{activeSection.subtitle}</p>
                      ) : null}
                    </div>
                    {activePage?.href ? (
                      <a
                        href={activePage.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={adminBtnSecondary}
                      >
                        Deschide pagina
                        <ExternalLink className="h-3 w-3" aria-hidden />
                      </a>
                    ) : null}
                  </div>
                </AdminCardHeader>
                {activeSection.toggles?.length ? (
                  <div className="space-y-3 border-b border-[#3e2723]/8 px-5 py-4 sm:px-6">
                    <p className={adminEyebrow}>Setări globale (RO)</p>
                    {activeSection.toggles.map((tog) => (
                      <label
                        key={tog.path}
                        className="flex cursor-pointer items-center justify-between gap-4 rounded-xl border border-[#3e2723]/8 bg-[#faf8f8] px-4 py-3"
                      >
                        <span className="text-sm text-[#3e2723]">{tog.label}</span>
                        <input
                          type="checkbox"
                          checked={readToggleEffective(draft, tog.path)}
                          onChange={(e) =>
                            setDraft((d) => ({
                              ...d,
                              ro: setDot(d.ro || {}, tog.path, e.target.checked),
                            }))
                          }
                          className="h-4 w-4 rounded border-[#3e2723]/20 accent-[#3e2723]"
                        />
                      </label>
                    ))}
                    {activeSection.globalFields?.map((field) => {
                      const def = readDefault(activeSection, "ro", field.path);
                      const eff = readGlobalSettingEffective(draft, field.path, def ?? 0);
                      const custom = hasDbOverride(draft, activeSection, "ro", field.path);
                      return (
                        <label
                          key={field.path}
                          className="block rounded-xl border border-[#3e2723]/8 bg-[#faf8f8] px-4 py-3"
                        >
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <span className="text-sm text-[#3e2723]">{field.label}</span>
                            {custom ? <AdminBadge variant="db">În DB</AdminBadge> : null}
                          </div>
                          <input
                            type="number"
                            min={field.min ?? 0}
                            max={field.max ?? 120}
                            value={eff}
                            onChange={(e) => {
                              const raw = e.target.value;
                              const next = raw === "" ? def ?? 0 : Number(raw);
                              setDraft((d) => ({
                                ...d,
                                ro: setDot(d.ro || {}, field.path, next),
                              }));
                            }}
                            className={`${adminInput} mt-2 max-w-[8rem]`}
                          />
                        </label>
                      );
                    })}
                  </div>
                ) : null}
                <div className="divide-y divide-[#3e2723]/8">
                  {(activeSection.fields || []).map((field) => {
                    const def = readDefault(activeSection, lang, field.path);
                    const eff = readEffective(activeSection, lang, draft, field.path);
                    const custom = hasDbOverride(draft, activeSection, lang, field.path);
                    const strVal = eff === undefined || eff === null ? "" : String(eff);
                    const ft = field.type || "text";
                    const uploadBusy = uploadKey === `${activeSection.id}:${field.path}`;

                    return (
                      <div
                        key={field.path}
                        className="grid min-w-0 gap-3 px-4 py-4 sm:grid-cols-12 sm:items-start sm:gap-4 sm:px-6 sm:py-5"
                      >
                        <div className="sm:col-span-4">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-medium text-[#3e2723]">{field.label}</span>
                            {custom ? <AdminBadge variant="db">În DB</AdminBadge> : <AdminBadge variant="code">Din cod</AdminBadge>}
                          </div>
                          {field.hint ? (
                            <p className="mt-1.5 text-[11px] leading-snug text-[#3e2723]/55">{field.hint}</p>
                          ) : null}
                          <p
                            className="mt-1.5 hidden line-clamp-2 break-all font-mono text-[10px] text-[#3e2723]/40 sm:block"
                            title={`Cod: ${String(def ?? "")} · Live: ${strVal}`}
                          >
                            Cod: {formatSourceSnippet(def)} · Live: {formatSourceSnippet(eff)}
                          </p>
                        </div>
                        <div className="sm:col-span-5">
                          {ft === "textarea" ? (
                            <textarea
                              id={`f-${activeSection.id}-${field.path}`}
                              name={`${activeSection.id}.${field.path}`}
                              value={strVal}
                              onChange={(e) =>
                                setDraft((d) =>
                                  applyDraftValue(d, activeSection, lang, field.path, e.target.value)
                                )
                              }
                              rows={3}
                              className={adminTextarea}
                            />
                          ) : (
                            <input
                              id={`f-${activeSection.id}-${field.path}`}
                              name={`${activeSection.id}.${field.path}`}
                              type={ft === "image" ? "url" : "text"}
                              value={strVal}
                              onChange={(e) =>
                                setDraft((d) =>
                                  applyDraftValue(d, activeSection, lang, field.path, e.target.value)
                                )
                              }
                              placeholder={ft === "image" ? "https://… sau /api/uploads/…" : ""}
                              className={adminInput}
                            />
                          )}
                          {ft === "image" && strVal ? (
                            <img
                              src={strVal}
                              alt=""
                              className="mt-2 max-h-24 w-auto max-w-full rounded-xl border border-[#3e2723]/10 object-contain"
                            />
                          ) : null}
                        </div>
                        <div className="flex flex-wrap items-center gap-2 sm:col-span-3 sm:justify-end">
                          {ft === "image" ? (
                            <label className={`${adminBtnSecondary} cursor-pointer px-3 py-1.5 text-[11px]`}>
                              <input
                                type="file"
                                accept="image/jpeg,image/png,image/webp,image/gif"
                                className="sr-only"
                                disabled={uploadBusy || saving}
                                onChange={(e) => onUploadForField(e, activeSection, field.path)}
                              />
                              {uploadBusy ? "…" : "Upload"}
                            </label>
                          ) : null}
                          <button
                            type="button"
                            disabled={!custom}
                            onClick={() =>
                              setDraft((d) => removeDraftValue(d, activeSection, lang, field.path))
                            }
                            className={`${adminBtnSecondary} px-3 py-1.5 text-[11px] disabled:opacity-30`}
                          >
                            Resetează
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {activeSection.listEditor ? (
                  <div className="border-t border-[#3e2723]/8 px-5 py-5 sm:px-6">
                    <ListEditor
                      label={activeSection.listEditor.itemLabel}
                      items={readEffectiveList(activeSection, lang, draft)}
                      itemFields={activeSection.listEditor.itemFields}
                      itemLabel={activeSection.listEditor.itemLabel}
                      minItems={activeSection.listEditor.minItems ?? 0}
                      maxItems={activeSection.listEditor.maxItems ?? 20}
                      onChange={(items) =>
                        setDraft((d) => applyListToDraft(d, activeSection, lang, items))
                      }
                      onUpload={(index, fieldKey, file) =>
                        onUploadForListItem(activeSection, index, fieldKey, file)
                      }
                      uploadBusyKey={
                        uploadKey?.startsWith(`${activeSection.id}:list:`)
                          ? uploadKey.replace(`${activeSection.id}:list:`, "")
                          : null
                      }
                    />
                  </div>
                ) : null}

                {Array.isArray(activeSection.listEditors) && activeSection.listEditors.length ? (
                  <div className="space-y-6 border-t border-[#3e2723]/8 px-5 py-5 sm:px-6">
                    {activeSection.listEditors.map((le) => (
                      <ListEditor
                        key={le.path}
                        label={le.itemLabel}
                        items={readEffectiveList({ ...activeSection, listEditor: le }, lang, draft)}
                        itemFields={le.itemFields}
                        itemLabel={le.itemLabel}
                        minItems={le.minItems ?? 0}
                        maxItems={le.maxItems ?? 20}
                        onChange={(items) =>
                          setDraft((d) => applyListToDraft(d, { ...activeSection, listEditor: le }, lang, items))
                        }
                        onUpload={(index, fieldKey, file) =>
                          onUploadForListItem({ ...activeSection, listEditor: le }, index, fieldKey, file)
                        }
                        uploadBusyKey={
                          uploadKey?.startsWith(`${activeSection.id}:list:`)
                            ? uploadKey.replace(`${activeSection.id}:list:`, "")
                            : null
                        }
                      />
                    ))}
                  </div>
                ) : null}
              </AdminCard>
            ) : null}
          </div>
        </div>
      )}

      </div>

      {msg ? (
        <p className="mt-4 rounded-xl border border-[#3e2723]/10 bg-white px-4 py-3 text-sm text-[#3e2723]/80">{msg}</p>
      ) : null}

      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-[#3e2723]/10 bg-white/95 px-4 py-3 shadow-[0_-12px_40px_-12px_rgba(62,39,35,0.2)] backdrop-blur-md pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="hidden max-w-xl text-xs leading-relaxed text-[#3e2723]/55 sm:block">
            {mainTab === "copy"
              ? "Câmpurile neincluse aici rămân în JSON expert. Reîncarcă din DB după editări în JSON."
              : mainTab === "seo"
                ? "SEO & share: titlu/descriere Google + imagine preview link."
                : "Logo, favicon și rețele sociale. Salvează pentru a aplica pe site."}
          </p>
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            className={`${adminBtnPrimary} w-full sm:w-auto`}
          >
            <Save className="h-4 w-4" aria-hidden />
            {saving ? "Se salvează…" : "Salvează în baza de date"}
          </button>
        </div>
      </div>
    </div>
  );
}
